import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const emptyPriceRange = () => ({ min_qty: '', max_qty: '', price: '' });

const resolveImageUrl = (img, baseUrl) => {
    if (!img) return '';
    return img.startsWith('http') ? img : baseUrl + img;
};

const NO_IMAGE = '/assets/images/no-img-avatar.png';

function EditProduct() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const formRef = useRef();


    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [brandId, setBrandId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [warehouseId, setWarehouseId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [partNumber, setPartNumber] = useState('');
    const [oePartNumber, setOePartNumber] = useState('');
    const [sku, setSku] = useState('');
    const [model, setModel] = useState('');
    const [deliveryTime, setDeliveryTime] = useState('');
    const [price, setPrice] = useState('');
    const [cost, setCost] = useState('');
    const [qty, setQty] = useState('100');
    const [year, setYear] = useState('');
    const [suitableFor, setSuitableFor] = useState('');
    const [specification, setSpecification] = useState('');
    const [isPublish, setIsPublish] = useState(false);
    const [featureImg, setFeatureImg] = useState(null);
    const [featureImgPreview, setFeatureImgPreview] = useState(null);
    const [existingImages, setExistingImages] = useState([]);
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [priceRanges, setPriceRanges] = useState([emptyPriceRange()]);
    const [baseUrl, setBaseUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    const buildFormData = () => {
        const data = new FormData();
        data.append('brand_id', brandId);
        data.append('category_id', categoryId);
        data.append('warehouse_id', warehouseId);
        data.append('title', title);

        if (description) data.append('description', description);
        if (partNumber) data.append('part_number', partNumber);
        if (oePartNumber) data.append('oe_part_number', oePartNumber);
        if (sku) data.append('sku', sku);
        if (model) data.append('model', model);
        if (deliveryTime) data.append('delivery_time', deliveryTime);
        if (price !== '') data.append('price', price);
        if (cost !== '') data.append('cost', cost);
        if (qty !== '') data.append('qty', qty);
        if (year) data.append('year', year);
        if (suitableFor) {
            const years = suitableFor.split(',').map((y) => Number(y.trim())).filter((y) => !isNaN(y));
            data.append('suitable_for', JSON.stringify(years));
        }
        if (specification) data.append('specification', specification);
        data.append('is_publish', isPublish ? 'true' : 'false');
        if (featureImg) data.append('feature_img', featureImg);
        images.forEach((file) => data.append('images', file));

        const validRanges = priceRanges.filter((r) => r.min_qty !== '' && r.max_qty !== '' && r.price !== '');
        if (validRanges.length) {
            const numericRanges = validRanges.map(r => ({
                min_qty: Number(r.min_qty),
                max_qty: Number(r.max_qty),
                price: Number(r.price)
            }));
            data.append('price_ranges', JSON.stringify(numericRanges));
        }

        return data;
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [productRes, brandsRes, categoriesRes, warehousesRes] = await Promise.all([
                    api.get(`/product/${slug}`),
                    api.get('/brand/list?limit=500&offset=0'),
                    api.get('/category/list?limit=500&offset=0'),
                    api.get('/warehouse/list?limit=500&offset=0')
                ]);

                if (brandsRes.status === 200) {
                    setBrands(brandsRes.data.brands || []);
                }
                if (categoriesRes.status === 200) {
                    setCategories(categoriesRes.data.categories || []);
                }
                if (warehousesRes.status === 200) {
                    setWarehouses(warehousesRes.data.warehouses || []);
                }

                if (productRes.status === 200) {
                    const imageBaseUrl = productRes.data.base_url || '';
                    const product = productRes.data.product;
                    setBaseUrl(imageBaseUrl);
                    setTitle(product.title || '');
                    setDescription(product.description || '');
                    setPartNumber(product.part_number || '');
                    setOePartNumber(product.oe_part_number || '');
                    setSku(product.sku || '');
                    setModel(product.model || '');
                    setDeliveryTime(product.delivery_time || '');
                    setPrice(product.price != null ? String(product.price) : '');
                    setCost(product.cost != null ? String(product.cost) : '');
                    setQty(product.qty != null ? String(product.qty) : '100');
                    setYear(product.year || '');
                    setSpecification(product.specification || '');
                    setIsPublish(product.is_publish === true);

                    const brandIdValue = product.brand_id?.$oid || product.brand_id?.toString?.() || product.brand?._id || '';
                    const categoryIdValue = product.category_id?.$oid || product.category_id?.toString?.() || product.category?._id || '';
                    const warehouseIdValue = product.warehouse_id?.$oid || product.warehouse_id?.toString?.() || '';
                    setBrandId(brandIdValue);
                    setCategoryId(categoryIdValue);
                    setWarehouseId(warehouseIdValue);

                    if (Array.isArray(product.suitable_for) && product.suitable_for.length) {
                        setSuitableFor(product.suitable_for.join(', '));
                    }

                    const productImages = Array.isArray(product.images) ? product.images : [];

                    if (product.feature_img) {
                        setFeatureImgPreview(resolveImageUrl(product.feature_img, imageBaseUrl));
                    } else if (productImages.length) {
                        setFeatureImgPreview(resolveImageUrl(productImages[0], imageBaseUrl));
                    } else {
                        setFeatureImgPreview(NO_IMAGE);
                    }

                    if (productImages.length) {
                        setExistingImages(productImages);
                    }

                    if (Array.isArray(product.price_ranges) && product.price_ranges.length) {
                        setPriceRanges(product.price_ranges);
                    }
                }

            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        const $ = window.jQuery;

        $.validator.addMethod('extension', function (value, element, allowedExtensions) {
            if (!value) return true;
            var extension = value.split('.').pop().toLowerCase();
            return $.inArray(extension, allowedExtensions) !== -1;
        }, 'Invalid file type');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    brand_id: { required: true },
                    category_id: { required: true },
                    warehouse_id: { required: false },
                    title: { required: true, maxlength: 300 },
                    feature_img: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'svg', 'webp']
                    },
                    images: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'svg', 'webp']
                    }
                },
                messages: {
                    brand_id: { required: "Please select a brand" },
                    category_id: { required: "Please select a category" },
                    warehouse_id: { required: "Please select a warehouse" },
                    title: {
                        required: "Please enter product title",
                        maxlength: "Title cannot exceed 300 characters"
                    },
                    feature_img: { extension: "Only jpg, jpeg, png, svg, webp allowed" },
                    images: { extension: "Only jpg, jpeg, png, svg, webp allowed" }
                },
                ignore: [],
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [slug]);

    const handleFeatureImgChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFeatureImg(file);
            setFeatureImgPreview(URL.createObjectURL(file));
        }
    };

    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length) {
            setImages(files);
            setImagePreviews(files.map((file) => URL.createObjectURL(file)));
        }
    };

    const updatePriceRange = (index, field, value) => {
        setPriceRanges((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const addPriceRange = () => {
        setPriceRanges((prev) => [...prev, emptyPriceRange()]);
    };

    const removePriceRange = (index) => {
        setPriceRanges((prev) => prev.filter((_, i) => i !== index));
    };

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            $(form)
                .find('.is-valid, .is-invalid')
                .removeClass('is-valid is-invalid');

            $(form)
                .find('label.error, .invalid-feedback')
                .remove();

            try {
                setSubmitting(true);
                const res = await api.put(
                    `/product/${slug}`,
                    buildFormData(),
                    {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
                if (res.status === 200) {
                    showToast("success", res.data.message, "Success");
                    navigate(ROUTES.PRODUCTS);
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.PRODUCTS}>Products</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/products/${slug}/edit`}>Edit Product</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Edit Product</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Brand<sup style={{ color: 'red' }}>*</sup></label>
                                        <select
                                            name="brand_id"
                                            className="form-control"
                                            value={brandId}
                                            onChange={(e) => setBrandId(e.target.value)}
                                        >
                                            <option value="">Select brand</option>
                                            {brands.map((brand) => (
                                                <option key={brand._id} value={brand._id}>
                                                    {brand.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Category<sup style={{ color: 'red' }}>*</sup></label>
                                        <select
                                            name="category_id"
                                            className="form-control"
                                            value={categoryId}
                                            onChange={(e) => setCategoryId(e.target.value)}
                                        >
                                            <option value="">Select category</option>
                                            {categories.map((category) => (
                                                <option key={category._id} value={category._id}>
                                                    {category.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Warehouse</label>
                                        <select
                                            name="warehouse_id"
                                            className="form-control"
                                            value={warehouseId}
                                            onChange={(e) => setWarehouseId(e.target.value)}
                                        >
                                            <option value="">Select warehouse</option>
                                            {warehouses.map((warehouse) => (
                                                <option key={warehouse._id} value={warehouse._id}>
                                                    {warehouse.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Title<sup style={{ color: 'red' }}>*</sup></label>
                                        <input
                                            name="title"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. Brake Pad Set"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Part Number</label>
                                        <input
                                            name="part_number"
                                            type="text"
                                            className="form-control"
                                            value={partNumber}
                                            onChange={(e) => setPartNumber(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">OEM</label>
                                        <input
                                            name="oe_part_number"
                                            type="text"
                                            className="form-control"
                                            value={oePartNumber}
                                            onChange={(e) => setOePartNumber(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">SKU</label>
                                        <input
                                            name="sku"
                                            type="text"
                                            className="form-control"
                                            value={sku}
                                            onChange={(e) => setSku(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Model</label>
                                        <input
                                            name="model"
                                            type="text"
                                            className="form-control"
                                            value={model}
                                            onChange={(e) => setModel(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Delivery Time</label>
                                        <input
                                            name="delivery_time"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. 2-3 days"
                                            value={deliveryTime}
                                            onChange={(e) => setDeliveryTime(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Price</label>
                                        <input
                                            name="price"
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={price}
                                            onChange={(e) => setPrice(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Cost</label>
                                        <input
                                            name="cost"
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={cost}
                                            onChange={(e) => setCost(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Quantity</label>
                                        <input
                                            name="qty"
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={qty}
                                            onChange={(e) => setQty(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Year</label>
                                        <input
                                            name="year"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. 2020"
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Suitable For (years)</label>
                                        <input
                                            name="suitable_for"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. 2011, 2012, 2013"
                                            value={suitableFor}
                                            onChange={(e) => setSuitableFor(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            name="description"
                                            className="form-control"
                                            rows={3}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Specification</label>
                                        <textarea
                                            name="specification"
                                            className="form-control"
                                            rows={3}
                                            value={specification}
                                            onChange={(e) => setSpecification(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <div className="form-check form-switch">
                                            <input
                                                id="is_publish"
                                                name="is_publish"
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isPublish}
                                                onChange={(e) => setIsPublish(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="is_publish">Publish</label>
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label className="form-label">Feature Image</label>
                                        <div className="input-group custom_file_input">
                                            <div className="form-file">
                                                <input
                                                    name="feature_img"
                                                    type="file"
                                                    className="form-file-input form-control"
                                                    onChange={handleFeatureImgChange}
                                                />
                                            </div>
                                            {/* <span className="input-group-text">Upload</span> */}
                                        </div>
                                        {featureImgPreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={featureImgPreview}
                                                    alt="Feature preview"
                                                    style={{ width: 100, height: 100, objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label className="form-label">Images</label>
                                        <div className="input-group custom_file_input">
                                            <div className="form-file">
                                                <input
                                                    name="images"
                                                    type="file"
                                                    multiple
                                                    className="form-file-input form-control"
                                                    onChange={handleImagesChange}
                                                />
                                            </div>
                                            {/* <span className="input-group-text">Upload</span> */}
                                        </div>
                                        {existingImages.length > 0 && imagePreviews.length === 0 && (
                                            <div className="mt-2 d-flex flex-wrap gap-2">
                                                {existingImages.map((img, index) => (
                                                    <img
                                                        key={index}
                                                        src={resolveImageUrl(img, baseUrl)}
                                                        alt={`Existing ${index + 1}`}
                                                        style={{ width: 80, height: 80, objectFit: 'contain' }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {existingImages.length === 0 && imagePreviews.length === 0 && (
                                            <div className="mt-2 d-flex flex-wrap gap-2">
                                                <img
                                                    src={NO_IMAGE}
                                                    alt="Placeholder"
                                                    style={{ width: 80, height: 80, objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                        {imagePreviews.length > 0 && (
                                            <div className="mt-2 d-flex flex-wrap gap-2">
                                                {imagePreviews.map((src, index) => (
                                                    <img
                                                        key={index}
                                                        src={src}
                                                        alt={`Preview ${index + 1}`}
                                                        style={{ width: 80, height: 80, objectFit: 'contain' }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Price Ranges</label>
                                        {priceRanges.map((row, index) => (
                                            <div className="row mb-2" key={index}>
                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="form-control"
                                                        placeholder="Min Qty"
                                                        value={row.min_qty}
                                                        onChange={(e) => updatePriceRange(index, 'min_qty', e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="form-control"
                                                        placeholder="Max Qty"
                                                        value={row.max_qty}
                                                        onChange={(e) => updatePriceRange(index, 'max_qty', e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control"
                                                        placeholder="Price (AED)"
                                                        value={row.price}
                                                        onChange={(e) => updatePriceRange(index, 'price', e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-2 d-flex gap-1">
                                                    {priceRanges.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger light"
                                                            onClick={() => removePriceRange(index)}
                                                        >
                                                            <i className="fas fa-minus"></i>
                                                        </button>
                                                    )}
                                                    {index === priceRanges.length - 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary light"
                                                            onClick={addPriceRange}
                                                        >
                                                            <i className="fas fa-plus"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submit
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.PRODUCTS} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditProduct;
